import { useTranslation } from 'react-i18next';
import { cn } from '$lib/lib/cn';
import { formatKrPerSqm } from '$lib/lib/formatters';
import type { ComparablesAggregate, ComparablesTarget } from '$lib/api/types';

export interface AggregateStatsProps {
  aggregate: ComparablesAggregate;
  target?: ComparablesTarget;
  /** Decision 10: dim to "stale" while a recompute is in flight. */
  stale?: boolean;
  /** Decision 10: 1px progress bar atop the aggregate panel during recompute. */
  recomputing?: boolean;
}

function StatCell({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 px-3 py-2 first:pl-0 border-r border-rule last:border-r-0">
      <span className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</span>
      <span className={cn('num text-md', accent ? 'text-accent' : 'text-fg')}>{value}</span>
    </div>
  );
}

export function AggregateStats({ aggregate, target, stale, recomputing }: AggregateStatsProps) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="comparables-aggregate"
      aria-label={t('comparables.aggregateAriaLabel')}
      className="relative overflow-hidden rounded-sm border border-rule bg-surface"
    >
      <div
        aria-hidden
        data-testid="aggregate-progress-bar"
        className={cn(
          'absolute left-0 right-0 top-0 h-px overflow-hidden bg-surface-3 transition-opacity',
          recomputing ? 'opacity-100' : 'opacity-0'
        )}
      >
        <span className="block h-full w-1/3 animate-[indeterminate_1.1s_ease-in-out_infinite] bg-accent" />
      </div>
      <div
        className={cn(
          'grid grid-cols-2 transition-[opacity,filter] duration-fast md:grid-cols-4',
          stale && 'opacity-65 saturate-50'
        )}
        data-stale={stale ? 'true' : 'false'}
      >
        <StatCell label={t('comparables.stats.median')} value={formatKrPerSqm(aggregate.median)} accent />
        <StatCell label={t('comparables.stats.p25')} value={formatKrPerSqm(aggregate.p25)} />
        <StatCell label={t('comparables.stats.p75')} value={formatKrPerSqm(aggregate.p75)} />
        <StatCell label={t('comparables.stats.count')} value={String(aggregate.count)} />
      </div>
      {target && (
        <div className="border-t border-rule bg-surface-2 px-3 py-1.5 text-2xs text-fg-muted">
          {target.kind === 'provisional' ? t('comparables.stats.targetProvisional') : t('comparables.stats.target')}{' '}
          <span className="num text-fg">{formatKrPerSqm(target.krPerSqm)}</span>
          {aggregate.median > 0 && (
            <>
              {' '}
              · {t('comparables.stats.vsMedian')}{' '}
              <span
                className={cn(
                  'num',
                  target.krPerSqm > aggregate.median ? 'text-warning' : 'text-positive'
                )}
              >
                {target.krPerSqm > aggregate.median ? '+' : ''}
                {Math.round(((target.krPerSqm - aggregate.median) / aggregate.median) * 100)}%
              </span>
            </>
          )}
        </div>
      )}
    </section>
  );
}
